import React, { useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { useLocation } from 'react-router-dom';

import {
  PlusOutlined,
} from "@ant-design/icons";

import commonComponents from "../../../components/common";
import itemsComponents from "../../../components/items";

import Filter from "./components/leaked-secrets/Filter";
import TableData from "./components/leaked-secrets/TableData";
import BoxData from "./components/leaked-secrets/BoxData";
import FormData from "./components/leaked-secrets/FormData";
import SecretFormData from "./components/secrets/FormData";

import { CipherType } from "../../../core-js/src/enums";

import {
  getRouterByLocation,
  scrollToTop,
  scrollEnd
} from '../../../utils/common';

import global from "../../../config/global";
import coreServices from "../../../services/core";
import leakedSecretServices from "../../../services/leaked-secret";

const ProjectLeakedSecrets = () => {
  const { PageHeader } = commonComponents;
  const { NoData, Pagination } = itemsComponents;
  const { t } = useTranslation();
  const location = useLocation();

  const currentPage = getRouterByLocation(location);
  const projectId = currentPage?.params?.project_id;

  const isMobile = useSelector((state) => state.system.isMobile)
  const isReadOnly = useSelector((state) => state.project.isReadOnly)
  const createSecret = useSelector((state) => state.onboarding.createSecret)

  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [secretFormVisible, setSecretFormVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [allTotal, setAllTotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [secrets, setSecrets] = useState([]);
  const [params, setParams] = useState({
    page: 1,
    size: global.constants.PAGE_SIZE,
    key: '',
  });

  useEffect(() => {
    if (createSecret) {
      handleOpenForm()
    }
  }, [createSecret])

  useEffect(() => {
    getAllTotal()
  }, [projectId])

  useEffect(() => {
    fetchData()
  }, [projectId, JSON.stringify(params)])

  useEffect(() => {
    setParams({
      ...params,
      page: 1,
      key: currentPage?.query?.key,
    })
  }, [currentPage?.query?.key])

  useEffect(() => {
    setParams({
      ...params,
      page: 1,
      size: global.constants.PAGE_SIZE
    })
  }, [isMobile])

  const handleChangePage = (page, size) => {
    scrollToTop();
    setParams({
      ...params,
      page,
      size
    })
  };

  const getAllTotal = async () => {
    await leakedSecretServices.list(projectId, {page: 1, size: 1}).then((response) => {
      setAllTotal(response.count)
    }).catch(() => {
      setAllTotal(0)
    })
  }

  const fetchData = async () => {
    setLoading(true);
    await leakedSecretServices.list(projectId, params).then(async (response) => {
      const secrets = await coreServices.decrypt_ciphers(response.results, CipherType.LeakedSecret);
      setTotal(response.count);
      setSecrets(secrets);
    }).catch(() => {
      setTotal(0);
      setSecrets([]);
    })
    setLoading(false);
  }

  const handleOpenForm = (item = null) => {
    setSelectedItem(item);
    setFormVisible(true)
  }

  const handleOpenSecretForm = (item = null) => {
    setSelectedItem(item);
    setSecretFormVisible(true)
  }

  return (
    <div
      className="leaked-secrets layout-content"
      onScroll={(e) => scrollEnd(e, params, total, setParams)}
    >
      <PageHeader
        title={t('leaked_secrets.title')}
        total={total} 
        subtitle={t('leaked_secrets.description')}
        actions={[
          {
            key: 'add',
            label: t('leaked_secrets.new'),
            type: 'primary',
            icon: <PlusOutlined />,
            hide: isReadOnly,
            click: () => handleOpenForm()
          },
        ]}
      />
      {
        allTotal > 0 && <div className="h-[55px] mt-6">
          <Filter
            params={params}
            setParams={(v) => setParams({ ...v, page: 1 })}
          />
        </div>
      }
      {
        total == 0 ? <NoData
          loading={loading}
          className={'mt-6'}
        /> : <>
          {
            isMobile ? <BoxData
              className="mt-4"
              loading={loading}
              data={secrets}
              params={params}
              onUpdate={handleOpenSecretForm}
            /> : <TableData
              className="mt-4"
              loading={loading}
              data={secrets}
              params={params}
              onUpdate={handleOpenSecretForm}
            />
          }
        </>
      }
      {
        total > global.constants.PAGE_SIZE && !isMobile && <Pagination
          params={params}
          total={total}
          onChange={handleChangePage}
        />
      }
      <FormData
        visible={formVisible}
        item={selectedItem}
        onClose={() => setFormVisible(false)}
      />
      <SecretFormData
        visible={secretFormVisible}
        leakedSecret={selectedItem}
        onReload={() => {}}
        onClose={() => setSecretFormVisible(false)}
      />
    </div>
  );
}

export default ProjectLeakedSecrets;